import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { ENGAGEMENT_STAGES, STAGE_LABELS } from '../../shared/stages';
import { ErrorNote, Spinner, StagePill, fmtDate } from '../components/ui';

interface EngagementListRow {
  id: string;
  company_name: string;
  industry: string | null;
  contact_name: string;
  contact_email: string;
  stage: string;
  recommended_offer: string | null;
  fit_score: number | null;
  urgency_score: number | null;
  hubspot_deal_id: string | null;
  created_at: number;
  updated_at: number;
}

interface Stats {
  byStage: Record<string, number>;
  outbox: Record<string, number>;
  last30d: number;
  hubspotConfigured: boolean;
}

export function Dashboard() {
  const [rows, setRows] = useState<EngagementListRow[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [stage, setStage] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ company_name: '', contact_name: '', contact_email: '', primary_goal: '' });
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const qs = new URLSearchParams();
      if (stage) qs.set('stage', stage);
      if (q) qs.set('q', q);
      const [list, s] = await Promise.all([
        api.get<{ engagements: EngagementListRow[] }>(`/api/engagements?${qs.toString()}`),
        api.get<Stats>('/api/stats'),
      ]);
      setRows(list.engagements);
      setStats(s);
    } catch {
      setError('Failed to load engagements.');
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stage]);

  async function createEngagement(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/engagements', newForm);
      setShowNew(false);
      setNewForm({ company_name: '', contact_name: '', contact_email: '', primary_goal: '' });
      await load();
    } catch {
      setError('Failed to create engagement.');
    } finally {
      setBusy(false);
    }
  }

  const active = (stats?.byStage &&
    Object.entries(stats.byStage).filter(([k]) => k !== 'closed_won' && k !== 'closed_lost').reduce((s, [, n]) => s + n, 0)) ?? 0;
  const pendingSync = stats?.outbox?.pending ?? 0;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-slate-50">Engagements</h1>
        <div className="ml-auto flex items-center gap-2">
          <input
            className="input !w-56"
            placeholder="Search company / contact…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
          />
          <select className="input !w-auto" value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">All stages</option>
            {ENGAGEMENT_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
          </select>
          <button className="btn-primary" onClick={() => setShowNew((v) => !v)}>+ New</button>
        </div>
      </div>

      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="card !p-4"><div className="text-2xl font-bold text-slate-50">{active}</div><div className="text-xs text-slate-500">Active engagements</div></div>
          <div className="card !p-4"><div className="text-2xl font-bold text-slate-50">{stats.last30d}</div><div className="text-xs text-slate-500">New in last 30 days</div></div>
          <div className="card !p-4"><div className="text-2xl font-bold text-slate-50">{stats.byStage.needs_review ?? 0}</div><div className="text-xs text-slate-500">Needs review</div></div>
          <div className="card !p-4">
            <div className={`text-sm font-bold ${stats.hubspotConfigured ? 'text-green-400' : 'text-amber-400'}`}>
              {stats.hubspotConfigured ? 'Connected' : 'Not configured'}
            </div>
            <div className="text-xs text-slate-500">
              HubSpot sync{pendingSync > 0 ? ` · ${pendingSync} queued` : ''}
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <form onSubmit={createEngagement} className="card mb-5 grid gap-3 sm:grid-cols-4">
          <input className="input" placeholder="Company name" required value={newForm.company_name}
                 onChange={(e) => setNewForm((f) => ({ ...f, company_name: e.target.value }))} />
          <input className="input" placeholder="Contact name" required value={newForm.contact_name}
                 onChange={(e) => setNewForm((f) => ({ ...f, contact_name: e.target.value }))} />
          <input className="input" type="email" placeholder="Contact email" required value={newForm.contact_email}
                 onChange={(e) => setNewForm((f) => ({ ...f, contact_email: e.target.value }))} />
          <button className="btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create engagement'}</button>
        </form>
      )}

      {error && <ErrorNote message={error} />}
      {rows === null ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="card py-16 text-center text-sm text-slate-500">
          No engagements yet. Share <code className="text-brand-300">/start</code> with prospects — every
          submission lands here with a fit score and a pre-call brief.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full">
            <thead className="bg-slate-900">
              <tr>
                <th className="th">Company</th>
                <th className="th">Contact</th>
                <th className="th">Stage</th>
                <th className="th">Fit</th>
                <th className="th">Urgency</th>
                <th className="th">CRM</th>
                <th className="th">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r) => (
                <tr key={r.id} className="bg-slate-950/40 transition-colors hover:bg-slate-900/60">
                  <td className="td">
                    <Link to={`/app/engagements/${r.id}`} className="font-semibold text-slate-100 hover:text-brand-300">
                      {r.company_name}
                    </Link>
                    <div className="text-xs text-slate-500">{r.industry ?? ''}</div>
                  </td>
                  <td className="td">
                    <div>{r.contact_name}</div>
                    <div className="text-xs text-slate-500">{r.contact_email}</div>
                  </td>
                  <td className="td"><StagePill stage={r.stage} /></td>
                  <td className="td font-semibold">{r.fit_score ?? '—'}</td>
                  <td className="td font-semibold">{r.urgency_score ?? '—'}</td>
                  <td className="td text-xs">{r.hubspot_deal_id ? <span className="text-green-400">synced</span> : <span className="text-slate-600">queued</span>}</td>
                  <td className="td text-xs text-slate-500">{fmtDate(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
